import { useMemo } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  Button,
  TableContainer,
  useToast,
  useColorModeValue,
  Container,
  Flex,
  HStack,
  VStack,
  Icon,
} from "@chakra-ui/react";
import { FiFileText } from "react-icons/fi";
import { FaTicketAlt, FaClock } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useBreakpointValue } from "@chakra-ui/react";
import { useLoaderData } from "react-router-dom";
import client from "../../../../helpers/Client";
import Link from "../../../../components/Link";
import urlToId from "../../../../helpers/UrlToId";
import { translateType } from "../../../../shared/libs/translate";
import { ServiceState } from "../../../../components/States";

export async function ServiceLoader() {
  const resp = await client.get("/attachments/", {
    params: { page: 1, page_size: 100, ordering: "-id", object_model: "ticket.ticket" },
    disableCache: true,
  });
  const data = resp.data;
  let items = [];
  if (Array.isArray(data)) {
    items = data;
  } else if (Array.isArray(data?.results)) {
    items = data.results;
  } else if (Array.isArray(data?.data)) {
    items = data.data;
  }
  return [items];
}

function ServiceFormsTable({ services }) {
  const toast = useToast();
  const { t } = useTranslation();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const headerBg = useColorModeValue(
    "linear(135deg, var(--chakra-colors-primary-600) 0%, var(--chakra-colors-primary-800) 100%)",
    "linear(135deg, var(--chakra-colors-primary-500) 0%, var(--chakra-colors-primary-700) 100%)"
  );
  const cardBg = useColorModeValue("white", "gray.800");
  const tableBg = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const pageBg = useColorModeValue(
    "linear(135deg, var(--chakra-colors-gray-50) 0%, var(--chakra-colors-gray-100) 100%)",
    "linear(135deg, var(--chakra-colors-gray-900) 0%, var(--chakra-colors-gray-800) 100%)"
  );
  const containerPadding = useBreakpointValue({ base: 4, md: 6, lg: 8 });
  const cardSpacing = useBreakpointValue({ base: 4, md: 6, lg: 8 });
  const rows = useMemo(() => {
    const arr = Array.isArray(services) ? services : [];
    return arr;
  }, [services]);

  const downloadServicePdf = async (ticketId) => {
    try {
      const resp = await client.get("/attachments/", {
        params: { object_model: "ticket.ticket", object_id: ticketId },
        disableCache: true,
      });
      const list = Array.isArray(resp?.data?.results)
        ? resp.data.results
        : Array.isArray(resp?.data)
        ? resp.data
        : [];
      if (!list.length) {
        toast({
          title: "Form PDF bulunamadı",
          description: "Bu servis kaydına ait PDF eklentisi bulunamadı.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      const preferred =
        list.find((a) =>
          String(a?.description || "").toLowerCase().includes("servis formu pdf")
        ) || list[0];
      let fileHref = preferred?.file_url || preferred?.file;
      if (!fileHref) {
        toast({
          title: "Geçersiz eklenti",
          description: "Eklenti bağlantısı bulunamadı.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      const base = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
      if (!/^https?:\/\//.test(fileHref)) {
        fileHref = base + (fileHref.startsWith("/") ? fileHref : `/${fileHref}`);
      }
      const fileResp = await client.get(fileHref, {
        responseType: "blob",
        disableCache: true,
      });
      const blob = fileResp?.data;
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = preferred?.file_name || "servis_formu.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error("PDF indirme hatası:", e);
      toast({
        title: "PDF indirilemedi",
        description: "Bağlantı sırasında bir hata oluştu (erişim veya ağ).",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box minH="120vh" bgGradient={pageBg} position="relative" overflow="hidden">
      <Box position="relative" zIndex={2} p={containerPadding}>
        <Container maxW={isMobile ? "full" : "8xl"} px={isMobile ? 2 : 8}>
          <VStack spacing={cardSpacing} align="stretch">
            <Box
              bgGradient={headerBg}
              borderRadius="xl"
              p={6}
              mb={6}
              shadow="md"
              color="white"
            >
              <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                <VStack align="start" spacing={2}>
                  <HStack spacing={3}>
                    <Icon as={FaTicketAlt} boxSize={8} color="white" />
                    <Text fontSize="3xl" fontWeight="700" color="white">
                      {t("navigation.serviceForms") || "Servis Formları"}
                    </Text>
                  </HStack>
                  <HStack spacing={6}>
                    <HStack spacing={2}>
                      <Icon as={FaTicketAlt} boxSize={4} />
                      <Text fontSize="md" opacity={0.9}>
                        {rows.length} / {rows.length} Form
                      </Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Icon as={FaClock} boxSize={4} />
                      <Text fontSize="md" opacity={0.9}>
                        Son Güncelleme: {new Date().toLocaleTimeString("tr-TR")}
                      </Text>
                    </HStack>
                  </HStack>
                </VStack>
              </Flex>
            </Box>
            <Box
              bg={cardBg}
              borderRadius="xl"
              shadow="lg"
              overflow="hidden"
              border="1px solid"
              borderColor={borderColor}
            >
              <TableContainer position="relative" zIndex={1} overflowX="auto" maxW="100%">
                <Table variant="simple" size={isMobile ? "sm" : "md"} minW={isMobile ? "800px" : "auto"}>
                  <Thead bg={tableBg}>
                    <Tr>
                      <Th>#</Th>
                      <Th>Ürün</Th>
                      <Th>Tip</Th>
                      <Th>Durum</Th>
                      <Th>İş Emri</Th>
                      <Th>Form</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.map((att) => {
                      const ticketId = att?.object_id;
                      const ticketLink = ticketId ? `/is-emri/${ticketId}` : null;
                      const fileName = att?.file_name || "servis_formu.pdf";
                      const attachmentId = urlToId(att?.url);
                      return (
                        <Tr
                          key={att.url || attachmentId}
                          _hover={{
                            bg: hoverBg,
                            transform: "translateY(-1px)",
                            shadow: "md",
                          }}
                          transition="all 0.2s ease"
                        >
                          <Td>
                            <Badge colorScheme="purple" variant="outline">
                              #{ticketId || "-"}
                            </Badge>
                          </Td>
                          <Td>
                            <Text noOfLines={2}>{att?.description || fileName}</Text>
                          </Td>
                          <Td>-</Td>
                          <Td>-</Td>
                          <Td>
                            {ticketLink ? (
                              <Link to={ticketLink}>İş Emri #{ticketId}</Link>
                            ) : (
                              <Text fontSize="xs" color="gray.500">Bağlı iş bulunamadı</Text>
                            )}
                          </Td>
                          <Td>
                            {ticketId ? (
                              <Box display="flex" gap={2} alignItems="center">
                                <Button
                                  size="xs"
                                  variant="outline"
                                  leftIcon={<FiFileText />}
                                  onClick={() => downloadServicePdf(ticketId)}
                                >
                                  PDF İndir
                                </Button>
                                {ticketLink && (
                                  <Button
                                    as={Link}
                                    to={ticketLink}
                                    size="xs"
                                    colorScheme="blue"
                                    variant="solid"
                                  >
                                    İş Emrine Git
                                  </Button>
                                )}
                              </Box>
                            ) : (
                              <Text fontSize="xs" color="gray.400">-</Text>
                            )}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          </VStack>
        </Container>
      </Box>
    </Box>
  );
}

export default function ServiceListCardView() {
  const [services] = useLoaderData();
  return <ServiceFormsTable services={services || []} />;
}
